import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { Button } from 'src/components/Button';
import { Card } from 'src/components/Card';
import { Divider } from 'src/components/Divider';
import { Stack } from 'src/components/Stack';
import { TextField } from 'src/components/TextField';
import { Typography } from 'src/components/Typography';
import { AppAlert } from 'src/shared/AppAlert';
import { PhoneFrame } from 'src/shared/PhoneFrame';

import { PHONE_PRESETS, PHONE_SCALES } from './_types';
import { buildPreviewAlert } from './appAlertFormUtils';

import type { AppAlertFormValues, BannerPreviewVariant } from './_types';

interface AppAlertPreviewPanelProps {
  values: AppAlertFormValues;
  previewImageUrls: Record<'heroImageUrl' | 'bgImageUrl', string | null>;
  bannerPreviewVariant: BannerPreviewVariant;
  setBannerPreviewVariant: (variant: BannerPreviewVariant) => void;
  selectedDeviceId: string;
  setSelectedDeviceId: (deviceId: string) => void;
  selectedScaleId: string;
  setSelectedScaleId: (scaleId: string) => void;
}

export const AppAlertPreviewPanel = ({
  values,
  previewImageUrls,
  bannerPreviewVariant,
  setBannerPreviewVariant,
  selectedDeviceId,
  setSelectedDeviceId,
  selectedScaleId,
  setSelectedScaleId,
}: AppAlertPreviewPanelProps) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'admin.platform.appAlertsForm',
  });
  const handlePreviewAction = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };
  const previewAlert = buildPreviewAlert(values);
  const effectivePreviewAlert = {
    ...previewAlert,
    heroImageUrl: previewImageUrls.heroImageUrl ?? previewAlert.heroImageUrl,
    bgImageUrl: previewImageUrls.bgImageUrl ?? previewAlert.bgImageUrl,
  };
  const selectedDevice =
    PHONE_PRESETS.find((preset) => preset.id === selectedDeviceId) ||
    PHONE_PRESETS[1];
  const selectedScale =
    PHONE_SCALES.find((option) => option.id === selectedScaleId) ||
    PHONE_SCALES[0];

  return (
    <Card sx={{ p: 3, position: { lg: 'sticky' }, top: { lg: 112 } }}>
      <Stack spacing={3}>
        <Typography variant="h6">
          {t('preview.title', {
            defaultValue: 'Preview',
          })}
        </Typography>

        <Box
          sx={{
            display: 'grid',
            gap: 2,
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          }}
        >
          <TextField
            label={t('preview.device', {
              defaultValue: 'Device',
            })}
            value={selectedDevice.id}
            onChange={(event) => setSelectedDeviceId(event.target.value)}
            options={PHONE_PRESETS.map((preset) => ({
              value: preset.id,
              label: t(`preview.devices.${preset.id}`, {
                defaultValue: preset.name,
              }),
            }))}
          />
          <TextField
            label={t('preview.scale', {
              defaultValue: 'Scale',
            })}
            value={selectedScale.id}
            onChange={(event) => setSelectedScaleId(event.target.value)}
            options={PHONE_SCALES.map((option) => ({
              value: option.id,
              label: t(`preview.scales.${option.id}`, {
                defaultValue: option.label,
              }),
            }))}
          />
        </Box>

        <Box
          sx={{
            minHeight: { xs: 320, lg: 720 },
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
          }}
        >
          {values.alertType === 'banner' ? (
            <PhoneFrame
              devicePreset={selectedDevice}
              scale={selectedScale.factor}
            >
              <Box
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  backgroundColor: 'background.paper',
                }}
              >
                <Box sx={{ height: '44px', flexShrink: 0 }} />
                <Box sx={{ width: '100%', flexShrink: 0 }}>
                  <AppAlert
                    alert={effectivePreviewAlert}
                    bannerVariant={bannerPreviewVariant}
                    onButtonClick={handlePreviewAction}
                  />
                </Box>
                <Box
                  sx={{
                    flexGrow: 1,
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    px: 3,
                  }}
                >
                  <Typography
                    variant="body2"
                    align="center"
                    color="text.secondary"
                  >
                    {t('preview.bannerDisplay', {
                      defaultValue:
                        'Banner Alerts will display on the Home page and Challenges page',
                    })}
                  </Typography>
                </Box>
                <Divider />
                <Box sx={{ height: '49px', flexShrink: 0 }} />
              </Box>
            </PhoneFrame>
          ) : (
            <PhoneFrame
              fullScreen
              devicePreset={selectedDevice}
              scale={selectedScale.factor}
            >
              <AppAlert
                alert={effectivePreviewAlert}
                onButtonClick={handlePreviewAction}
                onClose={() => {}}
              />
            </PhoneFrame>
          )}
        </Box>

        {values.alertType === 'banner' && (
          <Stack direction="row" spacing={2} justifyContent="center">
            <Button
              variant={
                bannerPreviewVariant === 'short' ? 'prominent' : 'bordered'
              }
              color={bannerPreviewVariant === 'short' ? 'primary' : 'neutral'}
              onClick={() => setBannerPreviewVariant('short')}
            >
              {t('preview.short', {
                defaultValue: 'Preview short banner',
              })}
            </Button>
            <Button
              variant={
                bannerPreviewVariant === 'tall' ? 'prominent' : 'bordered'
              }
              color={bannerPreviewVariant === 'tall' ? 'primary' : 'neutral'}
              onClick={() => setBannerPreviewVariant('tall')}
            >
              {t('preview.tall', {
                defaultValue: 'Preview tall banner',
              })}
            </Button>
          </Stack>
        )}
      </Stack>
    </Card>
  );
};
